import React, { useState, useRef, useEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BellIcon, Bars3Icon, XMarkIcon,
  UserPlusIcon, CheckCircleIcon, ArrowPathIcon, NewspaperIcon,
  ArrowTrendingUpIcon, ChartBarIcon, QuestionMarkCircleIcon,
  ArrowsRightLeftIcon, InformationCircleIcon, EnvelopeIcon,
} from "@heroicons/react/24/outline";
import logo from "../assets/finai_nobg.png";
import AnimatedThemeToggler from "./AnimatedThemeToggler";
import GlowButton from "./GlowButton";
import { AuthContext } from "../context/AuthContext";
import { NotificationContext } from "../context/NotificationContext";

// ── Type → monochrome line icon map ───────────────────────
const TYPE_CONFIG = {
  friend_request:   { Icon: UserPlusIcon },
  friend_accepted:  { Icon: CheckCircleIcon },
  portfolio_synced: { Icon: ArrowPathIcon },
  new_blog:         { Icon: NewspaperIcon },
  rank_change:      { Icon: ArrowTrendingUpIcon },
  news_genuine:     { Icon: ChartBarIcon },
  news_speculation: { Icon: QuestionMarkCircleIcon },
  sentiment_swing:  { Icon: ArrowsRightLeftIcon },
  contact_message:  { Icon: EnvelopeIcon },
};

function timeAgo(isoString) {
  const diff = Math.floor((Date.now() - new Date(isoString)) / 1000);
  if (diff < 60)    return "just now";
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Top nav consolidated 2026 redesign: portfolio sub-pages live as tabs
// inside <PortfolioAnalysis>; Insights surfaces as its own top-level route;
// Linked Accounts is reachable via Settings > Connected accounts.
const NAV_LINKS_AUTH = [
  { to: "/portfolio",   label: "Portfolio",   match: "/portfolio"   },
  { to: "/insights",    label: "Insights",    match: "/insights"    },
  { to: "/marketplace", label: "Marketplace", match: "/marketplace" },
  { to: "/blogs",       label: "Blogs",       match: "/blogs"       },
  { to: "/community",   label: "Community",   match: "/community"   },
];

// Advisor-mode nav. When the user is on /advisor/*, the primary nav
// swaps from investor links to these so there's only ONE source of
// navigation truth. The "ADVISOR" badge + the avatar-dropdown
// "Back to investor view" entry close the loop.
const NAV_LINKS_ADVISOR = [
  { to: "/advisor",         label: "Dashboard", match: "/advisor"         },
  { to: "/advisor/profile", label: "Profile",   match: "/advisor/profile" },
  { to: "/advisor/skills",  label: "Skills",    match: "/advisor/skills"  },
  { to: "/advisor/plans",   label: "Plans",     match: "/advisor/plans"   },
];

// Public nav shown to logged-out visitors (desktop row + mobile menu).
const NAV_LINKS_PUBLIC = [
  { to: "/marketplace", label: "Marketplace", match: "/marketplace" },
  { to: "/pricing",     label: "Pricing",     match: "/pricing"     },
  { to: "/blogs",       label: "Blogs",       match: "/blogs"       },
];

const Navbar = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user, profile, isAuthenticated, logout } = useContext(AuthContext);
  const { notifications, unreadCount, markRead, markAllRead } =
    useContext(NotificationContext);

  const [openProfile,  setOpenProfile]  = useState(false);
  const [openNotif,    setOpenNotif]    = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const profileRef = useRef();
  const notifRef   = useRef();

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setOpenProfile(false);
      if (notifRef.current && !notifRef.current.contains(e.target))
        setOpenNotif(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getUserInitials = () => {
    if (!user) return "?";
    if (user.first_name && user.last_name)
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    if (user.username) return user.username[0].toUpperCase();
    return "U";
  };

  // Avatar element used in both the desktop button and the mobile drawer.
  // Shows the uploaded photo if profile.avatar_url is set, otherwise falls
  // back to gradient initials.
  const renderAvatar = (size = 36) => {
    const src = profile?.avatar_url;
    const px = `${size}px`;
    if (src) {
      return (
        <img
          src={src}
          alt={user?.username || "avatar"}
          style={{ width: px, height: px }}
          className="rounded-full object-cover"
        />
      );
    }
    return (
      <div
        style={{ width: px, height: px }}
        className="rounded-full flex items-center justify-center font-semibold bg-indigo-600 text-white text-sm"
      >
        {getUserInitials()}
      </div>
    );
  };

  // Match the nav link's `match` path as a prefix so the Portfolio chip
  // stays highlighted on every /portfolio/* sub-route (Performance,
  // Holdings, Tax, Accounts).
  const isActive = (matchPath) => {
    if (matchPath === "/") return location.pathname === "/";
    return (
      location.pathname === matchPath ||
      location.pathname.startsWith(matchPath + "/")
    );
  };

  // The Dashboard tab matches /advisor exactly — without this special-case
  // it would match every /advisor/* sub-route as a prefix and stay active
  // while the user is on Skills, Plans, etc.
  const isAdvisorMode = location.pathname === "/advisor" || location.pathname.startsWith("/advisor/");
  const isAdvisorTabActive = (matchPath) => {
    if (matchPath === "/advisor") return location.pathname === "/advisor";
    return location.pathname.startsWith(matchPath);
  };
  const navLinks = isAdvisorMode ? NAV_LINKS_ADVISOR : NAV_LINKS_AUTH;
  const navIsActive = isAdvisorMode ? isAdvisorTabActive : isActive;

  return (
    <>
      {/* ── MAIN NAV BAR ────────────────────────────────── */}
      <nav
        className="
          fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-6xl rounded-2xl
          backdrop-blur-xl border
          bg-white/80 border-white/50 shadow-glass
          dark:bg-[#0f1624]/80 dark:border-white/10 dark:shadow-glass-dark
          text-gray-900 dark:text-white transition-all duration-300
        "
      >
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">

          {/* BRAND */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <img src={logo} alt="FinAI Logo" className="w-8 h-8 sm:w-10 sm:h-10" />
            <Link to="/" className="text-xl sm:text-2xl font-bold hover:opacity-80">
              FinAI
            </Link>
            {/* Advisor-mode context badge — makes the role switch unambiguous
                at a glance. Without it the nav looks identical except for the
                link labels, which is easy to miss while scrolling/editing. */}
            {isAdvisorMode && isAuthenticated && (
              <span className="ml-1 sm:ml-2 px-2 py-0.5 rounded-md text-[10px] sm:text-[11px] font-bold uppercase tracking-widest bg-indigo-500/15 text-indigo-500 border border-indigo-500/30">
                Advisor
              </span>
            )}
          </div>

          {/* DESKTOP NAV LINKS — hidden on mobile. Wrapped in an inner pill
              container so the nav group reads as a nested element within
              the navbar (segmented-control pattern: Linear, macOS Tahoe). */}
          {isAuthenticated && (
            <div className="hidden lg:flex flex-1 justify-center">
              <div
                className="
                  flex gap-1 p-1 rounded-xl
                  bg-black/5 dark:bg-white/[0.03]
                  border border-gray-200/60 dark:border-white/[0.06]
                "
              >
                {navLinks.map(({ to, label, match }) => {
                  const active = navIsActive(match);
                  return (
                    <Link
                      key={to}
                      to={to}
                      className={`whitespace-nowrap rounded-lg px-4 py-1.5 text-[15px] transition-colors ${
                        active
                          ? "nav-glass-active font-semibold"
                          : "font-medium text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white"
                      }`}
                    >
                      <span className="relative z-10">{label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
          {!isAuthenticated && (
            <div className="hidden lg:flex gap-5 text-sm text-gray-700 dark:text-gray-300">
              {NAV_LINKS_PUBLIC.map(({ to, label, match }) => (
                <Link
                  key={to}
                  to={to}
                  className={`hover:text-black dark:hover:text-white transition ${
                    isActive(match)
                      ? "text-indigo-500 dark:text-indigo-400 font-semibold"
                      : ""
                  }`}
                >
                  {label}
                </Link>
              ))}
            </div>
          )}

          {/* RIGHT SIDE */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {isAuthenticated ? (
              <>
                {/* THEME TOGGLE — sun ↔ moon morph, sits to the left of the
                    bell so the user can flip themes without diving into
                    Settings > Appearance. */}
                <AnimatedThemeToggler />

                {/* NOTIFICATIONS */}
                <div className="relative" ref={notifRef}>
                  <button
                    id="notif-bell-btn"
                    onClick={() => { setOpenNotif(!openNotif); setOpenProfile(false); setMobileMenuOpen(false); }}
                    className="relative hover:text-indigo-500 dark:hover:text-indigo-400 transition p-1"
                    aria-label="Notifications"
                  >
                    <BellIcon className="w-6 h-6" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full flex items-center justify-center text-[10px] text-white font-bold">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </button>

                  {openNotif && (
                    <div className="
                      absolute right-0 mt-3 w-80 sm:w-96 max-w-[calc(100vw-1.5rem)] rounded-2xl shadow-2xl
                      bg-white border border-gray-200 text-gray-900
                      dark:bg-gray-900 dark:border-gray-700 dark:text-white
                      animate-fadeIn overflow-hidden
                    ">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <h3 className="text-sm font-semibold">
                          Notifications
                          {unreadCount > 0 && (
                            <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                              {unreadCount} new
                            </span>
                          )}
                        </h3>
                        {unreadCount > 0 && (
                          <button onClick={markAllRead} className="text-xs text-indigo-500 hover:text-indigo-400 transition font-medium">
                            Mark all read
                          </button>
                        )}
                      </div>

                      <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                        {notifications.length === 0 ? (
                          <div className="py-8 text-center text-gray-400 text-sm">
                            <BellIcon className="w-7 h-7 mx-auto mb-1 text-gray-400" />
                            You're all caught up!
                          </div>
                        ) : (
                          notifications.map((notif) => {
                            const cfg = TYPE_CONFIG[notif.type] || { Icon: InformationCircleIcon };
                            const Icon = cfg.Icon;
                            return (
                              <div
                                key={notif.id}
                                id={`notif-item-${notif.id}`}
                                onClick={() => { if (!notif.is_read) markRead(notif.id); }}
                                className={`flex gap-3 px-4 py-3 cursor-pointer transition ${
                                  notif.is_read
                                    ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                                    : "bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30"
                                }`}
                              >
                                <Icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-700 dark:text-gray-200" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-gray-900 dark:text-white">{notif.title}</p>
                                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 leading-relaxed">{notif.message}</p>
                                  <p className="text-xs text-gray-400 mt-0.5">{timeAgo(notif.created_at)}</p>
                                </div>
                                {!notif.is_read && <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0 mt-1.5" />}
                              </div>
                            );
                          })
                        )}
                      </div>
                      {notifications.length > 0 && (
                        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-center">
                          <span className="text-xs text-gray-400">Updates every 30s · {notifications.length} total</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* PROFILE AVATAR */}
                <div className="relative hidden sm:block" ref={profileRef}>
                  <button
                    id="profile-menu-btn"
                    onClick={() => { setOpenProfile(!openProfile); setOpenNotif(false); setMobileMenuOpen(false); }}
                    className="w-9 h-9 rounded-full overflow-hidden hover:ring-2 ring-indigo-400 transition"
                  >
                    {renderAvatar(36)}
                  </button>

                  {openProfile && (
                    <div className="absolute right-0 mt-3 w-52 rounded-xl shadow-lg py-2 bg-white border border-gray-200 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-white animate-fadeIn">
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <p className="font-semibold text-sm">
                          {user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.username}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                      </div>
                      {profile?.is_advisor && !isAdvisorMode && (
                        <Link
                          to="/advisor"
                          className="block px-4 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => setOpenProfile(false)}
                        >
                          Advisor dashboard
                        </Link>
                      )}
                      {isAdvisorMode && (
                        <Link
                          to="/portfolio"
                          className="block px-4 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => setOpenProfile(false)}
                        >
                          ← Back to investor view
                        </Link>
                      )}
                      {profile?.is_staff && (
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm font-semibold text-amber-600 dark:text-amber-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => setOpenProfile(false)}
                        >
                          Admin dashboard
                        </Link>
                      )}
                      <Link to="/profile" className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setOpenProfile(false)}>Profile</Link>
                      <Link to="/settings" className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setOpenProfile(false)}>Settings</Link>
                      <Link to="/contact" className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setOpenProfile(false)}>Contact Support</Link>
                      <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700">Logout</button>
                    </div>
                  )}
                </div>

                {/* HAMBURGER — shown on mobile/tablet */}
                <button
                  id="hamburger-btn"
                  onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setOpenNotif(false); setOpenProfile(false); }}
                  className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                  aria-label="Menu"
                >
                  {mobileMenuOpen
                    ? <XMarkIcon className="w-6 h-6" />
                    : <Bars3Icon className="w-6 h-6" />
                  }
                </button>
              </>
            ) : (
              <div className="flex gap-2 sm:gap-3 items-center">
                <AnimatedThemeToggler />
                {/* Desktop login/signup */}
                <div className="hidden lg:flex gap-3 items-center">
                  <Link to="/login" className="px-4 py-1.5 rounded-xl border border-gray-300 text-gray-800 bg-white text-sm font-medium hover:bg-gray-50 transition shadow-sm dark:bg-transparent dark:border-white/20 dark:text-white dark:hover:bg-white/10">
                    Login
                  </Link>
                  <GlowButton to="/signup" size="sm">
                    SIGN UP
                  </GlowButton>
                </div>
                {/* Mobile hamburger — opens the public menu */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                  aria-label="Menu"
                >
                  {mobileMenuOpen
                    ? <XMarkIcon className="w-6 h-6" />
                    : <Bars3Icon className="w-6 h-6" />
                  }
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ── MOBILE FULL-SCREEN MENU ─────────────────────────── */}
      {mobileMenuOpen && isAuthenticated && (
        <div
          className="
            fixed inset-0 z-40 flex flex-col
            bg-white dark:bg-[#0f1624]
            animate-fadeIn overflow-y-auto
          "
        >
          {/* Top bar with close button */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                {renderAvatar(40)}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">
                  {user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.username}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition flex-shrink-0"
              aria-label="Close menu"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Nav links */}
          <div className="flex-1 py-2">
            {navLinks.map(({ to, label, match }) => (
              <Link
                key={to}
                to={to}
                className={`flex items-center px-6 py-4 text-base font-medium transition ${
                  navIsActive(match)
                    ? "text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                {label}
                {navIsActive(match) && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-500" />}
              </Link>
            ))}
            {profile?.is_advisor && !isAdvisorMode && (
              <Link
                to="/advisor"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-6 py-4 text-base font-semibold transition text-indigo-600 dark:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                Advisor dashboard
              </Link>
            )}
            {isAdvisorMode && (
              <Link
                to="/portfolio"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-6 py-4 text-base font-semibold transition text-indigo-600 dark:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                ← Back to investor view
              </Link>
            )}
            {profile?.is_staff && (
              <Link
                to="/admin"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-6 py-4 text-base font-semibold transition text-amber-600 dark:text-amber-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                Admin dashboard
              </Link>
            )}
          </div>

          {/* Settings + logout pinned to bottom */}
          <div className="px-5 py-6 border-t border-gray-200 dark:border-gray-700 flex gap-3 flex-shrink-0">
            <Link
              to="/settings"
              onClick={() => setMobileMenuOpen(false)}
              className="flex-1 text-center py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            >
              Settings
            </Link>
            <button
              onClick={handleLogout}
              className="flex-1 py-3 rounded-xl bg-red-500/10 text-red-500 border border-red-500/20 text-sm font-medium hover:bg-red-500/20 transition"
            >
              Logout
            </button>
          </div>
        </div>
      )}

      {/* ── MOBILE MENU (logged out) ────────────────────────── */}
      {mobileMenuOpen && !isAuthenticated && (
        <div
          className="
            fixed inset-0 z-40 flex flex-col
            bg-white dark:bg-[#0f1624]
            animate-fadeIn overflow-y-auto
          "
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-2">
              <img src={logo} alt="FinAI" className="w-8 h-8" />
              <span className="text-xl font-bold">FinAI</span>
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition flex-shrink-0"
              aria-label="Close menu"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 py-2">
            {NAV_LINKS_PUBLIC.map(({ to, label, match }) => (
              <Link
                key={to}
                to={to}
                className={`flex items-center px-6 py-4 text-base font-medium transition ${
                  isActive(match)
                    ? "text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                {label}
                {isActive(match) && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-500" />
                )}
              </Link>
            ))}
            <Link
              to="/contact"
              className="flex items-center px-6 py-4 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            >
              Contact
            </Link>
          </div>

          {/* Login + Sign up pinned to bottom */}
          <div className="px-5 py-6 border-t border-gray-200 dark:border-gray-700 flex gap-3 flex-shrink-0">
            <Link
              to="/login"
              className="flex-1 text-center py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            >
              Login
            </Link>
            <Link
              to="/signup"
              className="flex-1 text-center py-3 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 transition"
            >
              Sign up
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
